<!--
  Dismissible overlay for a cross-origin iframe that Safari's "Prevent cross-site tracking"
  may have quietly broken - today that's Polly's pricer (PollyPricerFlyout / PollyPricerView),
  but it's written against a plain url + label so the AI frames (ai.vue, gsAiWindow.vue) can
  reuse it if they ever hit the same wall.

  How it decides, in order - it bails at the first check that says no:
    1. Feature flag off?       -> silent. Ships default-off, see helpers/safariFrameNotice.js.
    2. Not a WebKit browser?   -> silent. Chrome/Edge/Firefox on desktop don't enforce ITP.
    3. Frame talked to us?     -> silent. Any postMessage from the framed origin means its
                                  scripts are alive, which is the only hard evidence available.
    4. Deadline passed in silence -> show the panel.

  Step 4 is a guess, so the panel sits *on top of* the frame and can be dismissed; it never
  replaces it. If Polly ever ships an explicit ready/heartbeat message, the parent can call
  markReady() on this component via ref and step 3 becomes deterministic.
-->
<template>
  <transition name="sfbn-fade">
    <div v-if="visible" class="sfbn" role="alert">
      <div class="sfbn__panel">
        <button type="button" class="sfbn__dismiss" title="Dismiss" aria-label="Dismiss" @click="dismiss">
          <i class="fa-solid fa-xmark"></i>
        </button>

        <div class="sfbn__head">
          <div class="sfbn__icon"><i class="fa-duotone fa-shield-exclamation"></i></div>
          <div>
            <div class="sfbn__title">{{ label }} may be blocked by Safari</div>
            <p class="sfbn__sub">
              Safari's <strong>Prevent cross-site tracking</strong> setting stops embedded tools
              from keeping their own sign-in, which can leave this panel blank or stuck on a
              login screen.
            </p>
          </div>
        </div>

        <div class="sfbn__actions">
          <button v-if="url" type="button" class="sfbn__btn sfbn__btn--primary" @click="openInTab">
            <i class="fa-solid fa-arrow-up-right-from-square"></i>
            Open in a new tab
          </button>
          <button type="button" class="sfbn__btn" @click="dismiss">It loaded fine &mdash; dismiss</button>
        </div>

        <p class="sfbn__hint">
          Opening it in its own tab works right away, without changing any settings.
        </p>

        <details class="sfbn__steps">
          <summary>Or turn the setting off so it works embedded</summary>

          <p class="sfbn__steps-label">On a Mac</p>
          <ol>
            <li>In Safari, choose <strong>Safari &rsaquo; Settings</strong> (or press <kbd>&#8984;</kbd><kbd>,</kbd>).</li>
            <li>Click <strong>Privacy</strong>.</li>
            <li>Uncheck <strong>Prevent cross-site tracking</strong>.</li>
            <li>Come back to this tab and reload the page.</li>
          </ol>

          <p class="sfbn__steps-label">On iPhone or iPad</p>
          <ol>
            <li>Open <strong>Settings</strong>, then <strong>Apps &rsaquo; Safari</strong> (just <strong>Safari</strong> on older iOS).</li>
            <li>Turn off <strong>Prevent Cross-Site Tracking</strong>.</li>
            <li>Return to Swift and reload the page.</li>
          </ol>

          <p class="sfbn__note">Chrome and Edge aren't affected by this setting.</p>
        </details>
      </div>
    </div>
  </transition>
</template>

<script>
import {
  isWebKitBrowser,
  safariFrameNoticeEnabled,
  frameNoticeDebug,
  openFreshUrlInTab
} from '../helpers/safariFrameNotice.js';

export default {
  name: 'SafariFrameBlockedNotice',
  // frame-ready: the framed origin posted a message, so its scripts are running. Fired
  // once per url, whether or not the notice itself is switched on.
  emits: ['frame-ready'],
  props: {
    // The frame's src. Empty means there is nothing to watch (still loading, or errored
    // out before a url came back), and changing it restarts the watch for the new frame.
    url: { type: String, default: '' },
    label: { type: String, default: 'This tool' },
    // Polly's first paint is well under this even on a cold cache; generous on purpose,
    // since being early here is worse than being late.
    delayMs: { type: Number, default: 8000 },
    // Escape hatch for a caller that already knows the frame is fine.
    disabled: { type: Boolean, default: false },
    // Returns (a promise of) a NEW launch url. Pricer urls are single-use, so the frame's own
    // url is already spent by the time anyone clicks "Open in a new tab". Without this the
    // button falls back to reusing `url`.
    getFreshUrl: { type: Function, default: null }
  },
  data() {
    return {
      visible: false,
      timer: null,
      watchedOrigin: '',
      readyReported: false
    };
  },
  watch: {
    url: { immediate: true, handler: 'restart' },
    disabled: 'restart'
  },
  mounted() {
    window.addEventListener('message', this.onFrameMessage);
  },
  beforeUnmount() {
    this.clearTimer();
    window.removeEventListener('message', this.onFrameMessage);
  },
  methods: {
    restart() {
      this.clearTimer();
      this.visible = false;
      this.watchedOrigin = '';
      this.readyReported = false;

      if (!this.url || this.disabled) return;

      // Needed for frame-ready even when the notice itself won't run below.
      this.watchedOrigin = this.originOf(this.url);

      if (!safariFrameNoticeEnabled()) return;

      if (!isWebKitBrowser()) {
        frameNoticeDebug('skipped: not a WebKit browser, ITP does not apply', {
          vendor: navigator.vendor
        });
        return;
      }


      frameNoticeDebug('watching frame', {
        origin: this.watchedOrigin || '(unparseable)',
        deadlineMs: this.delayMs
      });

      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.visible = true;
        frameNoticeDebug('deadline passed with no sign of life; showing notice');
      }, this.delayMs);
    },

    onFrameMessage(event) {
      if (!this.watchedOrigin) return;
      if (event.origin !== this.watchedOrigin) return;

      if (!this.readyReported) {
        this.readyReported = true;
        frameNoticeDebug('frame is alive; reporting frame-ready', { origin: event.origin });
        this.$emit('frame-ready');
      }
      if (!this.timer) return;

      // Payload deliberately ignored. Any message from the framed origin proves its
      // scripts are running and can reach us, which is all we're asking.
      frameNoticeDebug('frame spoke first; standing down', { origin: event.origin });
      this.clearTimer();
    },

    // Public. Call via ref if a caller gets a real ready signal from the frame.
    markReady() {
      this.clearTimer();
      this.visible = false;
    },

    originOf(url) {
      try {
        return new URL(url, window.location.href).origin;
      } catch (_) {
        // Unparseable: no message matching, but the deadline still runs.
        return '';
      }
    },

    clearTimer() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    },

    dismiss() {
      this.visible = false;
      this.clearTimer();
      frameNoticeDebug('dismissed by user');
    },

    openInTab() {
      // Top-level navigation makes the pricer a first-party site, which is both the
      // immediate fix and what Safari wants to have seen before it will ever trust the
      // embedded copy.
      if (this.getFreshUrl) {
        openFreshUrlInTab(this.getFreshUrl, this.label).catch((e) => {
          console.error('[SafariFrameNotice] could not get a fresh launch url', e);
        });
      } else {
        window.open(this.url, '_blank', 'noopener');
      }
      this.visible = false;
      frameNoticeDebug('opened pricer in a new tab', { fresh: !!this.getFreshUrl });
    }
  }
};
</script>

<style scoped>
.sfbn {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(248, 250, 253, 0.88);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}

.sfbn__panel {
  position: relative;
  width: 100%;
  max-width: 520px;
  max-height: 100%;
  overflow: auto;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.10);
  border-radius: 14px;
  box-shadow: 0 10px 40px rgba(15, 23, 42, 0.18);
  padding: 22px 24px;
}

.sfbn__dismiss {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: rgba(15, 23, 42, 0.45);
  cursor: pointer;
}
.sfbn__dismiss:hover { background: rgba(15, 23, 42, 0.06); color: rgba(15, 23, 42, 0.8); }

.sfbn__head { display: flex; gap: 14px; align-items: flex-start; padding-right: 26px; }
.sfbn__icon { font-size: 28px; color: #b7791f; flex: 0 0 auto; line-height: 1.2; }
.sfbn__title { font-size: 16px; font-weight: 700; color: #1f2a44; margin-bottom: 5px; }
.sfbn__sub { font-size: 13px; line-height: 1.55; color: #5b6478; margin: 0; }

.sfbn__actions { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 18px; }

.sfbn__btn {
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #fff;
  color: #1f2a44;
  border-radius: 10px;
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.sfbn__btn:hover { background: #f4f6fa; }
.sfbn__btn--primary {
  background: #123a71;
  border-color: #123a71;
  color: #fff;
}
.sfbn__btn--primary:hover { background: #0e2e5a; }

.sfbn__hint { font-size: 12px; color: #7b8397; margin: 10px 0 0; }

.sfbn__steps {
  margin-top: 16px;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  padding-top: 14px;
  font-size: 13px;
  color: #5b6478;
}
.sfbn__steps summary { cursor: pointer; font-weight: 600; color: #1f2a44; }
.sfbn__steps ol { margin: 6px 0 14px; padding-left: 20px; line-height: 1.7; }
.sfbn__steps-label {
  margin: 12px 0 2px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8b93a6;
}
.sfbn__note { font-size: 12px; color: #8b93a6; margin: 0; }
.sfbn__steps kbd {
  font: inherit;
  background: #f1f3f8;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 5px;
  padding: 0 5px;
  margin-right: 2px;
}

.sfbn-fade-enter-active, .sfbn-fade-leave-active { transition: opacity 0.18s ease; }
.sfbn-fade-enter-from, .sfbn-fade-leave-to { opacity: 0; }
</style>
